import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
    TrashIcon,
    UserIcon,
    ClipboardDocumentCheckIcon,
    ShieldCheckIcon,
    DocumentTextIcon,
    ExclamationCircleIcon,
    ArrowLeftIcon
} from '@heroicons/react/24/outline';

const API_URL = '/api/v1';

export default function CancellationRequest() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const certId = searchParams.get('certId');

    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState(null);
    const [certificate, setCertificate] = useState(null);
    const [beneficiary, setBeneficiary] = useState('');
    const [purpose, setPurpose] = useState('Disclosure for green energy tariff');

    useEffect(() => {
        if (certId) {
            const email = localStorage.getItem('userEmail') || 'admin@map.go';
            const role = localStorage.getItem('userRole') || 'ADMIN';
            const headers = { 'X-User-Email': email, 'X-User-Role': role };

            axios.get(`${API_URL}/certificates/${certId}`, { headers })
                .then(res => setCertificate(res.data))
                .catch(err => console.error(err));
        }
    }, [certId]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const email = localStorage.getItem('userEmail') || 'admin@map.go';
            const role = localStorage.getItem('userRole') || 'ADMIN';
            const headers = { 'X-User-Email': email, 'X-User-Role': role };

            // We use the same DELETE endpoint but ensure it completes the cancellation
            await axios.delete(`${API_URL}/certificates/${certId}?reason=${encodeURIComponent(purpose)}&beneficiary=${encodeURIComponent(beneficiary)}`, { headers });
            setSuccess(true);
        } catch (err) {
            setError(err.response?.data?.detail || 'Cancellation failed. Check regulatory permissions.');
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="max-w-xl mx-auto py-20 text-center animate-fade-in bg-white border border-emerald-100 shadow-2xl p-10">
                <div className="w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-6 border border-emerald-100">
                    <ShieldCheckIcon className="w-12 h-12 text-emerald-500" />
                </div>
                <h2 className="text-2xl font-black text-gray-800 mb-4 tracking-tighter uppercase">Certificates Redeemed</h2>
                <p className="text-gray-500 mb-8 leading-relaxed font-medium text-sm">
                    The electronic certificates have been permanently removed from circulation.
                    Your Cancellation Statement (EECS Annex 7) is available for download.
                </p>
                <div className="flex gap-4 justify-center">
                    <button
                        onClick={() => window.open(`${API_URL}/certificates/${certId}/cancellation-statement`, '_blank')}
                        className="bg-[#155e75] hover:bg-[#0e4e61] px-8 py-3 font-black text-white text-[11px] uppercase tracking-widest shadow-xl transition-all flex items-center gap-2"
                    >
                        <DocumentTextIcon className="w-4 h-4" />
                        Download PDF Statement
                    </button>
                    <button onClick={() => navigate('/certificates')} className="px-8 py-3 border border-gray-200 font-black text-gray-400 text-[11px] uppercase tracking-widest hover:bg-gray-50">
                        Return to Ledger
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-5xl mx-auto py-8">
            <div className="mb-10 flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-black text-gray-800 mb-1 uppercase tracking-tight">Request Batch Cancellation</h1>
                    <p className="text-xs text-gray-500 font-medium">Permanently redeem certificates for disclosure purposes</p>
                </div>
                <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-[10px] font-black text-gray-400 uppercase tracking-widest hover:text-gray-600 transition-all">
                    <ArrowLeftIcon className="w-4 h-4" /> Back
                </button>
            </div>

            {error && (
                <div className="mb-8 p-4 bg-red-50 border border-red-100 text-red-600 flex items-center gap-3">
                    <ExclamationCircleIcon className="w-5 h-5 flex-shrink-0" />
                    <p className="text-[10px] font-black uppercase tracking-widest">{error}</p>
                </div>
            )}

            {certificate ? (
                <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
                    <div className="lg:col-span-2 space-y-8">
                        <div className="bg-white border border-gray-200 shadow-xl p-10 space-y-10">
                            <div className="p-6 bg-red-50 border border-red-100 flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <TrashIcon className="w-6 h-6 text-red-500" />
                                    <div>
                                        <h4 className="text-gray-800 font-black text-xs uppercase tracking-widest">Volume to Redeem</h4>
                                        <p className="text-[10px] text-gray-400 font-mono font-bold tracking-tight">{certificate.gs1_id}</p>
                                    </div>
                                </div>
                                <p className="text-3xl font-black text-gray-800 tabular-nums">{parseFloat(certificate.output_mwh).toLocaleString()} <span className="text-xs text-gray-400">MWh</span></p>
                            </div>

                            <div className="space-y-6">
                                <div className="flex items-center gap-3 border-b border-gray-100 pb-4">
                                    <UserIcon className="w-6 h-6 text-[#155e75]" />
                                    <h3 className="text-sm font-black text-gray-800 uppercase tracking-widest">End-User Beneficiary Data (Annex 7)</h3>
                                </div>
                                <div className="space-y-4">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block ml-1">Beneficiary Full Name *</label>
                                    <input
                                        required
                                        type="text"
                                        value={beneficiary}
                                        onChange={(e) => setBeneficiary(e.target.value)}
                                        placeholder="e.g. Atlas Green Energy Consumption Unit"
                                        className="w-full bg-white border border-gray-300 p-4 text-xs font-bold text-gray-800 outline-none focus:border-[#155e75]"
                                    />
                                    <div className="grid grid-cols-2 gap-4 pt-4">
                                        <div>
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block ml-1 mb-2">Usage period start</label>
                                            <input required type="date" className="w-full bg-white border border-gray-300 p-4 text-xs font-bold text-gray-800" />
                                        </div>
                                        <div>
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block ml-1 mb-2">Usage period end</label>
                                            <input required type="date" className="w-full bg-white border border-gray-300 p-4 text-xs font-bold text-gray-800" />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-6">
                                <div className="flex items-center gap-3 border-b border-gray-100 pb-4">
                                    <ClipboardDocumentCheckIcon className="w-6 h-6 text-[#155e75]" />
                                    <h3 className="text-sm font-black text-gray-800 uppercase tracking-widest">Cancellation Purpose</h3>
                                </div>
                                <select
                                    value={purpose}
                                    onChange={(e) => setPurpose(e.target.value)}
                                    className="w-full bg-white border border-gray-300 p-4 text-xs font-bold text-gray-800 outline-none focus:border-[#155e75] appearance-none"
                                >
                                    <option>Disclosure for green energy tariff</option>
                                    <option>Voluntary offset (CSR)</option>
                                    <option>Governmental Support Objective</option>
                                </select>
                            </div>

                            <div className="pt-6">
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full py-5 bg-red-600 hover:bg-red-700 transition-all font-black text-white text-[11px] uppercase tracking-widest shadow-xl flex items-center justify-center gap-3"
                                >
                                    {loading ? 'Redeeming Assets...' : 'Execute Permanent Cancellation'}
                                </button>
                                <p className="text-center text-[10px] text-gray-400 mt-6 uppercase font-black tracking-widest leading-relaxed italic">
                                    Warning: Action is irreversible. Certificates will be permanently removed from circulation.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-6">
                        <div className="bg-gray-50 border border-gray-200 p-6 shadow-inner">
                            <h4 className="text-[10px] font-black text-[#155e75] flex items-center gap-2 mb-4 uppercase tracking-widest">
                                <ShieldCheckIcon className="w-5 h-5" />
                                Regulatory Audit
                            </h4>
                            <p className="text-xs text-gray-500 leading-relaxed font-medium">
                                As per EECS Fact Sheet 16, a Cancellation Statement is the only legal proof of green energy consumption.
                                Minting duplicates or sharing the certificate after cancellation is a violation of AIB standards.
                            </p>
                        </div>
                    </div>
                </form>
            ) : (
                <div className="py-20 text-center text-gray-400 uppercase tracking-widest font-black text-xs italic bg-white border border-dashed border-gray-200">
                    No certificate selected for redemption.
                </div>
            )}
        </div>
    );
}
